import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import { useT } from '../../i18n/LocaleContext';
import { formatDateInput } from '../../utils/format';
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES, METHODS } from '../../data/financeStore';

const emptyForm = (type = 'income') => ({
  type,
  date: formatDateInput(new Date()),
  amount: '',
  category: type === 'income' ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0],
  source: '',
  destination: '',
  method: 'cash',
  reference: '',
  description: '',
});

export default function FinanceFormModal({ isOpen, onClose, onSubmit, initial, defaultType }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setForm(initial ? { ...emptyForm(initial.type), ...initial, amount: String(initial.amount) } : emptyForm(defaultType));
    setErrors({});
  }, [isOpen, initial, defaultType]);

  const categories = form.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c, label: t(`finance.categories.${c}`) })),
    [categories, t]
  );
  const methodOptions = useMemo(() => METHODS.map((m) => ({ value: m, label: t(`finance.methods.${m}`) })), [t]);

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setType = (type) => {
    setForm((prev) => ({
      ...prev,
      type,
      category: (type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).includes(prev.category)
        ? prev.category
        : (type === 'income' ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0]),
    }));
  };

  const validate = () => {
    const next = {};
    const amount = Number(form.amount);
    if (!form.date) next.date = t('validation.required');
    if (!form.amount || Number.isNaN(amount) || amount <= 0) next.amount = t('finance.amount_invalid');
    if (!form.source.trim()) next.source = t('validation.required');
    if (!form.destination.trim()) next.destination = t('validation.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    onSubmit({
      ...form,
      amount: Math.round(Number(form.amount)),
      source: form.source.trim(),
      destination: form.destination.trim(),
      reference: form.reference.trim(),
      description: form.description.trim(),
    });
  };

  const income = form.type === 'income';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('finance.edit_entry') : t('finance.new_entry')}
      subtitle={t('finance.form_subtitle')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit}>{isEdit ? t('common.save') : t('finance.record')}</Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="finance-type-switch" role="radiogroup" aria-label={t('finance.field_type')}>
          {['income', 'expense'].map((type) => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={form.type === type}
              className={`finance-type-btn finance-type-btn--${type} ${form.type === type ? 'is-active' : ''}`}
              onClick={() => setType(type)}
            >
              <span aria-hidden>{type === 'income' ? '↓' : '↑'}</span>
              {t(`finance.types.${type}`)}
            </button>
          ))}
        </div>

        <div className="form-row form-row--2">
          <FormField label={t('finance.field_date')} htmlFor="fin-date" required error={errors.date}>
            <Input id="fin-date" type="date" value={form.date} onChange={set('date')} invalid={Boolean(errors.date)} />
          </FormField>
          <FormField label={t('finance.field_amount')} htmlFor="fin-amount" required error={errors.amount}>
            <div className="input-suffix">
              <Input id="fin-amount" type="number" min={1} step={1} value={form.amount} onChange={set('amount')} invalid={Boolean(errors.amount)} />
              <span className="input-suffix__unit">{t('common.currency_rwf')}</span>
            </div>
          </FormField>
        </div>

        <div className="form-row form-row--2">
          <FormField label={t('finance.field_category')} htmlFor="fin-category">
            <Select id="fin-category" options={categoryOptions} value={form.category} onChange={set('category')} />
          </FormField>
          <FormField label={t('finance.field_method')} htmlFor="fin-method">
            <Select id="fin-method" options={methodOptions} value={form.method} onChange={set('method')} />
          </FormField>
        </div>

        <div className="form-row form-row--2">
          <FormField
            label={income ? t('finance.field_source_income') : t('finance.field_source_expense')}
            htmlFor="fin-source"
            required
            error={errors.source}
            hint={income ? t('finance.hint_source_income') : t('finance.hint_source_expense')}
          >
            <Input id="fin-source" value={form.source} onChange={set('source')} invalid={Boolean(errors.source)} />
          </FormField>
          <FormField
            label={income ? t('finance.field_destination_income') : t('finance.field_destination_expense')}
            htmlFor="fin-destination"
            required
            error={errors.destination}
            hint={income ? t('finance.hint_destination_income') : t('finance.hint_destination_expense')}
          >
            <Input id="fin-destination" value={form.destination} onChange={set('destination')} invalid={Boolean(errors.destination)} />
          </FormField>
        </div>

        <FormField label={t('finance.field_reference')} htmlFor="fin-reference" hint={t('finance.hint_reference')}>
          <Input id="fin-reference" value={form.reference} onChange={set('reference')} />
        </FormField>
        <FormField label={t('finance.field_description')} htmlFor="fin-description" hint={t('common.optional')}>
          <Textarea id="fin-description" rows={3} value={form.description} onChange={set('description')} />
        </FormField>
      </form>
    </Modal>
  );
}
